import type { TOC } from '@ember/component/template-only';
import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import {
  PrismBadge,
  PrismCard,
  PrismIconBrand,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import type { Review } from 'lumen/models';

import styles from './review-card.module.css';

interface HotelReviewsReviewCardSignature {
  Args: {
    isSelected: boolean;
    review: Review;
    selectReviewForDetail: (reviewId: string) => void;
  };
}

const handleKeyDown = (
  selectReviewForDetail: (reviewId: string) => void,
  reviewId: string | null,
  event: KeyboardEvent,
): void => {
  if (reviewId && (event.key === 'Enter' || event.key === ' ')) {
    event.preventDefault();
    event.stopPropagation();
    selectReviewForDetail(reviewId);
  }
};

<template>
  <PrismCard
    aria-selected={{if @isSelected "true" "false"}}
    class="cursor-pointer {{if @isSelected styles.card--selected}}"
    data-test-id={{concat "review-" @review.id}}
    tabindex="0"
    {{on "click" (fn @selectReviewForDetail @review.id)}}
    {{on "keydown" (fn handleKeyDown @selectReviewForDetail @review.id)}}
  >
    <:header>
      <div class="flex w-full items-center justify-between gap-300">
        <h4
          class="line-clamp-3"
          title={{@review.displayTitle}}
        >{{@review.displayTitle}}</h4>

        {{#unless @review.isBookingdotcomScoreOnly}}
          {{#if @review.isPendingApproval}}
            <div class={{styles.badge-pending}}>
              <PrismBadge
                @badgeType="warning"
                @label={{t "review_manager.review.badge.pending"}}
              />
            </div>
          {{else if @review.hasReply}}
            <PrismBadge
              @badgeType="success"
              @label={{t "review_manager.review.badge.replied"}}
            />
          {{else}}
            <PrismBadge
              @badgeType="info"
              @label={{t "review_manager.review.badge.new"}}
            />
          {{/if}}
        {{/unless}}
      </div>
    </:header>
    <:content>
      <div class="flex w-full items-center justify-between">
        <div class="flex items-center gap-100">
          <div class="text-neutral-default flex items-center">
            {{! template-lint-disable no-bare-strings }}
            <span class="text-500--regular">{{@review.score}}</span><span
              class="text-200--regular"
            >/10</span>
            {{! template-lint-enable no-bare-strings }}
          </div>
          <PrismIconBrand @name={{@review.otaId}} />
        </div>
        <span
          class="text-200--regular subdued-text first-letter:uppercase"
        >{{@review.formattedDateFromNow}}</span>
      </div>
    </:content>
  </PrismCard>
</template> satisfies TOC<HotelReviewsReviewCardSignature>;
